import { useEffect, useRef, useState } from 'react';
import { LockSimple, Plus, WarningCircle, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { FILTERABLE_FIELDS } from '../../config/filterable-fields';
import {
  useInsertCustomFieldDefinition,
  useUpdateCustomFieldDefinition,
} from '../../hooks/use-custom-field-definitions';
import {
  OPTION_ATTRIBUTE_TYPES,
  RANGE_ATTRIBUTE_TYPES,
  REST_ATTRIBUTE_TYPES,
  type CustomFieldDefinition,
  type RestAttributeType,
} from '../../types/custom-field-definitions.types';
import { getRestAttributeType, REST_TYPE_LABELS, toCreateAttributeRequest } from '../../utils/attribute-definition-mapper';
import {
  getAttributeApiErrorMessage,
  isDuplicateAttributeKeyError,
} from '../../utils/attribute-errors';
import { isValidAttributeKey, slugifyFieldKey } from '../../utils/slugify-field-key';

export type CustomFieldFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingField?: CustomFieldDefinition;
};

const KEY_HELP_DEFAULT = 'Auto-filled from the label — camelCase, letters and numbers only. Fixed once saved.';
const KEY_HELP_INVALID = 'Use camelCase letters and numbers only, starting with a lowercase letter.';
const DUPLICATE_KEY_MESSAGE = 'That key is already in use by another attribute.';
const SAVE_ERROR_MESSAGE = 'Something went wrong saving this field. Try Save again.';
const OPTIONS_TOO_FEW_MESSAGE = 'Add at least two non-empty options.';
// `,` separates filter values and `;` separates import values, so a multi-select option cannot hold either.
const OPTIONS_SEPARATOR_MESSAGE = 'Multi-select options cannot contain a comma or semicolon.';
const MULTI_ENUM_RESERVED = /[,;]/;

const fieldClassName =
  'h-10 rounded-md border-neutral-200 bg-white px-3 text-sm text-neutral-900 shadow-none placeholder:text-neutral-400 focus-visible:border-primary-600 focus-visible:ring-2 focus-visible:ring-primary-600/30';

export const CustomFieldFormModal = ({
  open,
  onOpenChange,
  editingField,
}: CustomFieldFormModalProps) => {
  const { toast } = useToast();
  const isEditMode = Boolean(editingField);
  const { mutateAsync: insertField, isPending: isInserting } = useInsertCustomFieldDefinition();
  const { mutateAsync: updateField, isPending: isUpdating } = useUpdateCustomFieldDefinition();
  const isSaving = isInserting || isUpdating;
  const keyManuallyEdited = useRef(false);

  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [type, setType] = useState<RestAttributeType>('string');
  const [enumOptions, setEnumOptions] = useState<string[]>(['', '']);
  const [required, setRequired] = useState(false);
  const [isRangeFilterable, setIsRangeFilterable] = useState(false);
  const [labelError, setLabelError] = useState(false);
  const [keyError, setKeyError] = useState('');
  const [enumError, setEnumError] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    keyManuallyEdited.current = isEditMode;
    setLabel(editingField?.Label ?? '');
    setKey(editingField?.Key ?? '');
    setType(editingField ? getRestAttributeType(editingField) : 'string');
    const existingOptions = editingField?.Options?.filter(Boolean) ?? [];
    setEnumOptions(existingOptions.length >= 2 ? existingOptions : ['', '']);
    setRequired(editingField?.Required === true);
    // Legacy `NumberRange` definitions predate `IsRangeFilterable`, so infer the flag from the data type.
    setIsRangeFilterable(
      editingField?.IsRangeFilterable === true || editingField?.DataType === 'NumberRange'
    );
    setLabelError(false);
    setKeyError('');
    setEnumError('');
    setServerError(null);
  }, [open, editingField, isEditMode]);

  const hasOptions = OPTION_ATTRIBUTE_TYPES.includes(type);
  const showRange = RANGE_ATTRIBUTE_TYPES.includes(type);

  const handleLabelChange = (value: string) => {
    setLabel(value);
    setLabelError(false);
    if (!isEditMode && !keyManuallyEdited.current) {
      setKey(slugifyFieldKey(value));
      setKeyError('');
    }
  };

  const handleKeyChange = (value: string) => {
    keyManuallyEdited.current = true;
    setKey(value);
    setKeyError('');
  };

  const handleTypeChange = (value: RestAttributeType) => {
    setType(value);
    setEnumError('');
    if (!RANGE_ATTRIBUTE_TYPES.includes(value)) {
      setIsRangeFilterable(false);
    }
    if (OPTION_ATTRIBUTE_TYPES.includes(value) && enumOptions.length < 2) {
      setEnumOptions(['', '']);
    }
  };

  const validate = (): boolean => {
    const trimmedLabel = label.trim();
    const trimmedKey = key.trim();
    const labelBad = !trimmedLabel;
    let nextKeyError = '';

    if (!isEditMode) {
      if (!trimmedKey || !isValidAttributeKey(trimmedKey)) {
        nextKeyError = KEY_HELP_INVALID;
      } else if (FILTERABLE_FIELDS.some((field) => field.key === trimmedKey)) {
        nextKeyError = DUPLICATE_KEY_MESSAGE;
      }
    }

    const cleanedOptions = enumOptions.map((option) => option.trim()).filter(Boolean);
    let nextEnumError = '';
    if (hasOptions && cleanedOptions.length < 2) {
      nextEnumError = OPTIONS_TOO_FEW_MESSAGE;
    } else if (type === 'multienum' && cleanedOptions.some((option) => MULTI_ENUM_RESERVED.test(option))) {
      nextEnumError = OPTIONS_SEPARATOR_MESSAGE;
    }

    setLabelError(labelBad);
    setKeyError(nextKeyError);
    setEnumError(nextEnumError);

    return !labelBad && !nextKeyError && !nextEnumError;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setServerError(null);

    const trimmedLabel = label.trim();
    const trimmedKey = key.trim();
    const cleanedOptions = enumOptions.map((option) => option.trim()).filter(Boolean);

    try {
      if (isEditMode && editingField?.Key) {
        await updateField({
          key: editingField.Key,
          input: {
            label: trimmedLabel,
            ...(hasOptions ? { enumOptions: cleanedOptions } : {}),
          },
        });
      } else {
        await insertField(
          toCreateAttributeRequest({
            key: trimmedKey,
            label: trimmedLabel,
            type,
            enumOptions: cleanedOptions,
            required,
            isRangeFilterable,
          })
        );
      }

      toast({
        variant: 'success',
        title: isEditMode ? 'Custom field updated.' : 'Custom field created.',
      });
      onOpenChange(false);
    } catch (error) {
      if (!isEditMode && isDuplicateAttributeKeyError(error)) {
        setKeyError(getAttributeApiErrorMessage(error) ?? DUPLICATE_KEY_MESSAGE);
        return;
      }
      setServerError(getAttributeApiErrorMessage(error) ?? SAVE_ERROR_MESSAGE);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        overlayClassName="bg-black/40"
        className="flex max-h-[90vh] w-full max-w-[460px] flex-col gap-0 overflow-hidden rounded-lg border-neutral-200 p-0 shadow-e4 sm:max-w-[460px]"
      >
        <DialogHeader className="flex h-14 flex-none flex-row items-center justify-between space-y-0 border-b border-neutral-200 px-5">
          <DialogTitle className="text-base font-semibold leading-6 text-neutral-900">
            {isEditMode ? 'Edit attribute' : 'New attribute'}
          </DialogTitle>
          <DialogClose className="inline-flex size-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800">
            <X className="size-[18px]" aria-hidden />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <div className="mb-3">
            <Label htmlFor="af-label" className="mb-1.5 block text-[13px] font-medium text-neutral-800">
              Label
            </Label>
            <Input
              id="af-label"
              autoFocus
              placeholder="e.g. Renewal date"
              value={label}
              maxLength={100}
              className={cn(fieldClassName, labelError && 'border-error focus-visible:border-error focus-visible:ring-error/25')}
              onChange={(event) => handleLabelChange(event.target.value)}
            />
            {labelError && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-error" role="alert">
                <WarningCircle className="size-3.5" aria-hidden />
                Label is required.
              </p>
            )}
          </div>

          <div className="mb-3">
            <Label htmlFor="af-key" className="mb-1.5 block text-[13px] font-medium text-neutral-800">
              Key
            </Label>
            <Input
              id="af-key"
              placeholder="e.g. renewalDate"
              value={key}
              disabled={isEditMode}
              className={cn(
                fieldClassName,
                'font-mono',
                Boolean(keyError) && 'border-error focus-visible:border-error focus-visible:ring-error/25'
              )}
              onChange={(event) => handleKeyChange(event.target.value)}
            />
            {isEditMode ? (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-neutral-600">
                <LockSimple className="size-3.5" aria-hidden />
                Keys stay fixed after creation.
              </p>
            ) : (
              <p
                id="af-key-help"
                className={cn('mt-1.5 text-xs', keyError ? 'flex items-center gap-1 text-error' : 'text-neutral-600')}
                role={keyError ? 'alert' : undefined}
              >
                {keyError ? <WarningCircle className="size-3.5" aria-hidden /> : null}
                {keyError || KEY_HELP_DEFAULT}
              </p>
            )}
          </div>

          <div className="mb-3">
            <Label htmlFor="af-type" className="mb-1.5 block text-[13px] font-medium text-neutral-800">
              Type
            </Label>
            <Select value={type} onValueChange={(value) => handleTypeChange(value as RestAttributeType)} disabled={isEditMode}>
              <SelectTrigger id="af-type" className={cn(fieldClassName, 'focus:ring-primary-600/30')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REST_ATTRIBUTE_TYPES.map((attributeType) => (
                  <SelectItem key={attributeType} value={attributeType}>
                    {REST_TYPE_LABELS[attributeType]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isEditMode && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-neutral-600">
                <LockSimple className="size-3.5" aria-hidden />
                Type stays fixed after creation.
              </p>
            )}
          </div>

          {showRange && (
            <label className="mb-3 flex items-center gap-2 text-sm text-neutral-800">
            <Checkbox
              checked={isRangeFilterable}
              disabled={isEditMode}
              className="size-[18px] rounded-[4px] border-neutral-300 data-[state=checked]:border-primary-600 data-[state=checked]:bg-primary-600"
              onCheckedChange={(checked) => setIsRangeFilterable(checked === true)}
            />
            Range filter
              {isEditMode && (
                <span className="text-[11px] font-normal text-neutral-400">(set at creation only)</span>
              )}
            </label>
          )}
          {showRange && !isEditMode && (
            <p className="-mt-2 mb-3 text-xs text-neutral-600">
              Use a from–to filter instead of a single value. Fixed once saved.
            </p>
          )}

          {hasOptions && (
            <div className="mb-3">
              <Label className="mb-1.5 block text-[13px] font-medium text-neutral-800">Options</Label>
              <div className="space-y-2">
                {enumOptions.map((option, index) => (
                  <div key={`enum-option-${index}`} className="flex items-center gap-2">
                    <Input
                      value={option}
                      placeholder="Option value"
                      className={fieldClassName}
                      onChange={(event) => {
                        const next = [...enumOptions];
                        next[index] = event.target.value;
                        setEnumOptions(next);
                        setEnumError('');
                      }}
                    />
                    <button
                      type="button"
                      className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
                      aria-label={`Remove option ${index + 1}`}
                      onClick={() => {
                        if (enumOptions.length <= 2) return;
                        setEnumOptions(enumOptions.filter((_, optionIndex) => optionIndex !== index));
                      }}
                    >
                      <X className="size-4" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-1 h-8 px-2 text-neutral-700"
                onClick={() => setEnumOptions((current) => [...current, ''])}
              >
                <Plus className="size-3.5" aria-hidden />
                Add option
              </Button>
              {enumError && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-error" role="alert">
                  <WarningCircle className="size-3.5" aria-hidden />
                  {enumError}
                </p>
              )}
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-neutral-800">
            <Checkbox
              checked={required}
              disabled={isEditMode}
              className="size-[18px] rounded-[4px] border-neutral-300 data-[state=checked]:border-primary-600 data-[state=checked]:bg-primary-600"
              onCheckedChange={(checked) => setRequired(checked === true)}
            />
            Required
            {isEditMode && (
              <span className="text-[11px] font-normal text-neutral-400">(set at creation only)</span>
            )}
          </label>

          {serverError && (
            <div className="mt-3 flex items-start gap-2.5 rounded-lg bg-error-background px-3 py-2.5">
              <WarningCircle className="mt-0.5 size-4 shrink-0 text-error" aria-hidden />
              <p className="text-xs text-error">{serverError}</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-none flex-row items-center justify-end gap-2 border-t border-neutral-200 px-5 py-4 sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            className="h-10 px-4 text-neutral-800 hover:bg-neutral-100"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
            disabled={isSaving}
            loading={isSaving}
            onClick={() => void handleSave()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
